import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import ParticipantProcessView from '../components/ParticipantProcessView';
import OutcomeFollowups from '../components/participant/OutcomeFollowups';
import AdaptationsSection from '../components/participant/AdaptationsSection';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, User, Mail, Phone, Building2, Target,
  CalendarCheck, FileText, AlertTriangle, Clock,
  CheckCircle2, XCircle, MinusCircle, TrendingUp, Plus,
  Download, Trash2, ShieldAlert, Award
} from 'lucide-react';
import { Input } from '../components/ui/input';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const ALLVARLIGHET_CONFIG = {
  lag: { label: 'Låg', className: 'bg-success/20 text-success' },
  medel: { label: 'Medel', className: 'bg-primary/20 text-[#333333]' },
  hog: { label: 'Hög', className: 'bg-destructive/20 text-destructive' },
};

const DEV_STATUS_CONFIG = {
  oppen: { label: 'Öppen', className: 'bg-blue-100 text-blue-700' },
  utreds: { label: 'Utreds', className: 'bg-orange-100 text-orange-700' },
  atgardad: { label: 'Åtgärdad', className: 'bg-success/20 text-success' },
  stangd: { label: 'Stängd', className: 'bg-muted text-muted-foreground' },
};

const OCN_STATUS_CONFIG = {
  pagaende:  { label: 'Pågående',  className: 'bg-blue-100 text-blue-700' },
  godkand:   { label: 'Godkänd',   className: 'bg-green-100 text-green-700' },
  underkand: { label: 'Underkänd', className: 'bg-red-100 text-red-700' },
  avbruten:  { label: 'Avbruten',  className: 'bg-gray-100 text-gray-500' },
};

const OCN_NIVA_CONFIG = {
  bas:       { label: 'Bas',       className: 'bg-gray-100 text-gray-600' },
  mellan:    { label: 'Mellan',    className: 'bg-primary/20 text-[#333]' },
  avancerad: { label: 'Avancerad', className: 'bg-gray-700 text-white' },
};

const TYP_LABELS = {
  franvaro_utan_anledning: 'Frånvaro utan anledning',
  missad_aktivitet: 'Missad aktivitet',
  regelbrott: 'Regelbrott',
  ovrigt: 'Övrigt',
};

// M6 — utfallsregistrering vid avslut
const CLOSURE_REASONS = [
  'Fullföljd process',
  'Övergång till arbete',
  'Övergång till studier',
  'Övergång till annan insats',
  'Avbrott',
  'Ej påbörjad',
  'Administrativt avslut',
  'Annan orsak',
];

const CLOSURE_REASON_TO_CATEGORY = {
  'Övergång till arbete': 'arbete',
  'Övergång till studier': 'studier',
  'Övergång till annan insats': 'annan_insats',
  'Fullföljd process': 'fullfoljt',
  'Avbrott': 'avbrott',
  'Ej påbörjad': 'ingen_overgang',
  'Administrativt avslut': 'ingen_overgang',
  'Annan orsak': 'ingen_overgang',
};

const OUTCOME_CATEGORY_LABELS = {
  arbete: 'Övergång till arbete',
  studier: 'Övergång till studier',
  annan_insats: 'Övergång till annan insats',
  fullfoljt: 'Fullföljd process',
  avbrott: 'Avbrott',
  ingen_overgang: 'Ingen tydlig övergång',
};

const EMPLOYMENT_TYPE_LABELS = {
  tillsvidare: 'Tillsvidare',
  visstid: 'Visstid',
  vikariat: 'Vikariat',
  provanstallning: 'Provanställning',
  egen_foretagare: 'Egen företagare',
  annat: 'Annat',
};

const EMPLOYMENT_SCOPE_LABELS = {
  heltid: 'Heltid (100 %)',
  deltid_75: 'Deltid (~75 %)',
  deltid_50: 'Deltid (~50 %)',
  deltid_25: 'Deltid (~25 %)',
  timanstalld: 'Timanställd',
};

const EDUCATION_TYPE_LABELS = {
  gymnasium: 'Gymnasium',
  komvux: 'Komvux',
  yrkesutbildning: 'Yrkesutbildning',
  hogskola: 'Högskola/Universitet',
  folkbildning: 'Folkbildning',
  sfi: 'SFI',
  annat: 'Annat',
};

const emptyCloseForm = () => ({
  closure_reason: '',
  closure_date: new Date().toISOString().split('T')[0],
  status_comment: '',
  outcome_category: '',
  outcome_employer: '',
  outcome_employment_type: '',
  outcome_employment_scope: '',
  outcome_education_type: '',
  outcome_education_provider: '',
  outcome_start_date: '',
  outcome_notes: '',
});

const statusLabel = (status) => {
  const s = (status || '').toLowerCase();
  if (['present', 'närvarande', 'narvarande'].includes(s)) return { label: 'Närvarande', color: 'bg-success/20 text-success', icon: CheckCircle2 };
  if (['absent', 'frånvarande', 'franvarande'].includes(s)) return { label: 'Frånvarande', color: 'bg-destructive/20 text-destructive', icon: XCircle };
  return { label: status, color: 'bg-muted text-muted-foreground', icon: MinusCircle };
};

const timelineIcon = (type) => {
  switch (type) {
    case 'participant_created': return <User size={16} />;
    case 'goal_created': return <Target size={16} />;
    case 'attendance': return <CalendarCheck size={16} />;
    case 'meeting_note': return <FileText size={16} />;
    default: return <Clock size={16} />;
  }
};

const timelineColor = (type) => {
  switch (type) {
    case 'participant_created': return 'bg-primary/20 text-[#333333]';
    case 'goal_created': return 'bg-blue-100 text-blue-700';
    case 'attendance': return 'bg-success/20 text-success';
    case 'meeting_note': return 'bg-purple-100 text-purple-700';
    default: return 'bg-muted text-muted-foreground';
  }
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' });
};

const ParticipantProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user?.role !== 'read_only_assigner' && user?.role !== 'read_only_participant';

  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [allDeviations, setAllDeviations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('oversikt');
  const [deviationFormOpen, setDeviationFormOpen] = useState(false);
  const [devForm, setDevForm] = useState({ typ: '', allvarlighetsgrad: 'medel', beskrivning: '' });
  const [devSubmitting, setDevSubmitting] = useState(false);

  const [gdprDeleteOpen, setGdprDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [gdprDeleting, setGdprDeleting] = useState(false);

  const [ocnValidations, setOcnValidations] = useState([]);
  const [ocnFormOpen, setOcnFormOpen] = useState(false);
  const [ocnForm, setOcnForm] = useState({ kompetensomrade: 'Kommunikation', niva: 'bas', bedomare: user?.name || '', valideringsmalsattning: '', startdatum: new Date().toISOString().split('T')[0] });
  const [ocnCreating, setOcnCreating] = useState(false);

  // M6 — utfall vid avslut
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeForm, setCloseForm] = useState(emptyCloseForm());
  const [closeSubmitting, setCloseSubmitting] = useState(false);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'customer_admin';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [overviewRes, timelineRes, devRes, ocnRes] = await Promise.all([
          api.get(`/participants/${id}/overview`),
          api.get(`/participants/${id}/timeline`),
          api.get(`/participants/${id}/avvikelser`),
          api.get(`/participants/${id}/ocn-validations`),
        ]);
        setOverview(overviewRes.data);
        setTimeline(timelineRes.data);
        setAllDeviations(devRes.data);
        setOcnValidations(ocnRes.data);
      } catch (error) {
        toast.error('Kunde inte hämta deltagardata');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleCreateDeviation = async (e) => {
    e.preventDefault();
    if (!devForm.typ || !devForm.beskrivning) {
      toast.error('Fyll i typ och beskrivning');
      return;
    }
    setDevSubmitting(true);
    try {
      const res = await api.post('/avvikelser', {
        participant_id: id,
        typ: devForm.typ,
        allvarlighetsgrad: devForm.allvarlighetsgrad,
        beskrivning: devForm.beskrivning,
      });
      setAllDeviations(prev => [res.data, ...prev]);
      toast.success('Avvikelse registrerad');
      setDeviationFormOpen(false);
      setDevForm({ typ: '', allvarlighetsgrad: 'medel', beskrivning: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte registrera avvikelse');
    } finally {
      setDevSubmitting(false);
    }
  };

  const handleGdprExport = async () => {
    try {
      const res = await api.get(`/participants/${id}/gdpr-export`);
      const data = res.data;
      const p = data.participant || {};
      const exportDate = new Date().toLocaleDateString('sv-SE');
      const fileName = `gdpr_export_${(p.name || id).replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`;

      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const PRIMARY = [249, 207, 63];   // #F9CF3F
      const DARK    = [51, 51, 51];     // #333333
      const GRAY    = [120, 120, 120];
      const PAGE_W  = 210;
      const MARGIN  = 18;
      const CONTENT_W = PAGE_W - MARGIN * 2;
      let y = 0;

      const newPage = () => { doc.addPage(); y = 20; };
      const checkY = (needed = 12) => { if (y + needed > 275) newPage(); };

      // ── Header bar ──
      doc.setFillColor(...PRIMARY);
      doc.rect(0, 0, PAGE_W, 18, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(...DARK);
      doc.text('AIUS Control — GDPR Dataexport', MARGIN, 12);
      y = 26;

      // ── Meta ──
      doc.setFontSize(9);
      doc.setTextColor(...GRAY);
      doc.text(`Exportdatum: ${exportDate}   |   Deltagare: ${p.name || '—'}`, MARGIN, y);
      y += 10;

      // ── Section helper ──
      const sectionHeader = (title) => {
        checkY(14);
        doc.setFillColor(...PRIMARY);
        doc.rect(MARGIN, y, CONTENT_W, 7, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...DARK);
        doc.text(title, MARGIN + 3, y + 5);
        y += 11;
      };

      const row = (label, value) => {
        checkY(8);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...DARK);
        doc.text(`${label}:`, MARGIN, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...GRAY);
        const lines = doc.splitTextToSize(String(value || '—'), CONTENT_W - 42);
        doc.text(lines, MARGIN + 42, y);
        y += lines.length * 5 + 2;
      };

      // ── Grundinfo ──
      sectionHeader('Grundinformation');
      row('Namn',          p.name);
      row('E-post',        p.email);
      row('Telefon',       p.phone);
      row('Bakgrund',      p.background);
      row('Kompetenser',   p.skills);
      row('Mål',           p.goals);
      row('Registrerad',   p.created_at ? new Date(p.created_at).toLocaleDateString('sv-SE') : '');
      y += 4;

      // ── Mål ──
      const goals = data.goals || [];
      sectionHeader(`Mål (${goals.length})`);
      if (goals.length === 0) { doc.setFontSize(9); doc.setTextColor(...GRAY); doc.text('Inga mål registrerade.', MARGIN, y); y += 7; }
      goals.forEach((g, i) => {
        checkY(16);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...DARK);
        doc.text(`${i + 1}. ${g.title || g.beskrivning || '—'}`, MARGIN, y); y += 5;
        row('Status', g.status); row('Måldatum', g.target_date ? new Date(g.target_date).toLocaleDateString('sv-SE') : '');
      });
      y += 4;

      // ── Närvaro ──
      const att = data.attendance || [];
      sectionHeader(`Närvaro (${att.length} poster)`);
      if (att.length === 0) { doc.setFontSize(9); doc.setTextColor(...GRAY); doc.text('Ingen närvaro registrerad.', MARGIN, y); y += 7; }
      att.slice(0, 50).forEach(a => {
        checkY(7);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...GRAY);
        const dateStr = a.date ? new Date(a.date).toLocaleDateString('sv-SE') : '—';
        doc.text(`${dateStr}  —  ${a.status || '—'}`, MARGIN, y); y += 5;
      });
      if (att.length > 50) { doc.setFontSize(8); doc.setTextColor(...GRAY); doc.text(`... och ${att.length - 50} till.`, MARGIN, y); y += 5; }
      y += 4;

      // ── Mötesanteckningar ──
      const notes = data.meeting_notes || [];
      sectionHeader(`Mötesanteckningar (${notes.length})`);
      if (notes.length === 0) { doc.setFontSize(9); doc.setTextColor(...GRAY); doc.text('Inga anteckningar.', MARGIN, y); y += 7; }
      notes.forEach((n, i) => {
        checkY(18);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...DARK);
        doc.text(`${i + 1}. ${n.rubrik || '—'}  (${n.motesdatum ? new Date(n.motesdatum).toLocaleDateString('sv-SE') : '—'})`, MARGIN, y); y += 5;
        if (n.sammanfattning) {
          const lines = doc.splitTextToSize(n.sammanfattning, CONTENT_W);
          doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...GRAY);
          checkY(lines.length * 4 + 2);
          doc.text(lines, MARGIN, y); y += lines.length * 4 + 2;
        }
      });
      y += 4;

      // ── Avvikelser ──
      const devs = data.deviations || [];
      sectionHeader(`Avvikelser (${devs.length})`);
      if (devs.length === 0) { doc.setFontSize(9); doc.setTextColor(...GRAY); doc.text('Inga avvikelser.', MARGIN, y); y += 7; }
      devs.forEach((d, i) => {
        checkY(16);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...DARK);
        doc.text(`${i + 1}. ${d.typ || '—'}  —  ${d.allvarlighetsgrad || ''}`, MARGIN, y); y += 5;
        row('Beskrivning', d.beskrivning); row('Status', d.status);
      });

      // ── Footer on each page ──
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7); doc.setTextColor(...GRAY);
        doc.text(`AIUS Control — Konfidentiell GDPR-export — Sida ${i} av ${pageCount}`, MARGIN, 290);
      }

      doc.save(`${fileName}.pdf`);
      toast.success('GDPR-export (PDF) nedladdad');
    } catch (err) {
      console.error('GDPR PDF error:', err);
      toast.error('Kunde inte exportera data');
    }
  };

  const participantName = overview?.participant?.name || '';
  const expectedConfirmPhrase = participantName ? `RADERA ${participantName}` : '';

  const handleGdprDelete = async () => {
    if (!expectedConfirmPhrase || deleteConfirmText !== expectedConfirmPhrase) return;
    setGdprDeleting(true);
    try {
      await api.delete(`/participants/${id}/gdpr-delete`, {
        data: { confirm_phrase: expectedConfirmPhrase },
      });
      toast.success('Deltagaren är anonymiserad enligt GDPR Art. 17');
      navigate('/participants');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Radering misslyckades');
      setGdprDeleting(false);
    }
  };

  // M6 — öppna utfallsdialog (förifyll från befintlig participant)
  const openCloseDialog = () => {
    const p = overview?.participant || {};
    setCloseForm({
      closure_reason: p.closure_reason || '',
      closure_date: p.closure_date || new Date().toISOString().split('T')[0],
      status_comment: p.status_comment || '',
      outcome_category: p.outcome_category || '',
      outcome_employer: p.outcome_employer || '',
      outcome_employment_type: p.outcome_employment_type || '',
      outcome_employment_scope: p.outcome_employment_scope || '',
      outcome_education_type: p.outcome_education_type || '',
      outcome_education_provider: p.outcome_education_provider || '',
      outcome_start_date: p.outcome_start_date || '',
      outcome_notes: p.outcome_notes || '',
    });
    setCloseDialogOpen(true);
  };

  const handleCloseSubmit = async (e) => {
    e.preventDefault();
    if (!closeForm.closure_reason || !closeForm.closure_date) {
      toast.error('Avslutsorsak och avslutsdatum krävs');
      return;
    }
    setCloseSubmitting(true);
    try {
      const payload = {
        status: 'Avslutad',
        closure_reason: closeForm.closure_reason,
        closure_date: closeForm.closure_date,
        status_comment: closeForm.status_comment || null,
      };
      // Skicka bara utfallsfält som faktiskt fyllts i
      if (closeForm.outcome_category) {
        payload.outcome_category = closeForm.outcome_category;
        if (closeForm.outcome_category === 'arbete') {
          payload.outcome_employer = closeForm.outcome_employer;
          if (closeForm.outcome_employment_type) payload.outcome_employment_type = closeForm.outcome_employment_type;
          if (closeForm.outcome_employment_scope) payload.outcome_employment_scope = closeForm.outcome_employment_scope;
        }
        if (closeForm.outcome_category === 'studier') {
          if (closeForm.outcome_education_type) payload.outcome_education_type = closeForm.outcome_education_type;
          if (closeForm.outcome_education_provider) payload.outcome_education_provider = closeForm.outcome_education_provider;
        }
        if (closeForm.outcome_start_date) payload.outcome_start_date = closeForm.outcome_start_date;
        if (closeForm.outcome_notes) payload.outcome_notes = closeForm.outcome_notes;
      }
      const res = await api.patch(`/participants/${id}/status`, payload);
      setOverview(prev => prev ? { ...prev, participant: res.data } : prev);
      toast.success(closeForm.outcome_category ? 'Avslut och utfall registrerade' : 'Deltagaren avslutad');
      setCloseDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte avsluta deltagaren');
    } finally {
      setCloseSubmitting(false);
    }
  };

  // Auto-synka outcome_category när closure_reason ändras
  const setCloseReason = (reason) => {
    setCloseForm(prev => ({
      ...prev,
      closure_reason: reason,
      outcome_category: CLOSURE_REASON_TO_CATEGORY[reason] || prev.outcome_category,
    }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Laddar deltagarprofil...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!overview) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <p className="text-muted-foreground text-lg">Deltagaren hittades inte.</p>
          <Button variant="outline" onClick={() => navigate('/participants')}>
            <ArrowLeft size={16} className="mr-2" /> Tillbaka
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const { participant, client_name, goals, attendance_last_30_days, attendance_rate, meeting_notes, deviations } = overview;

  const handleCreateOcn = async (e) => {
    e.preventDefault();
    setOcnCreating(true);
    try {
      const res = await api.post('/ocn-validations', {
        participant_id: id,
        ...ocnForm,
        startdatum: new Date(ocnForm.startdatum).toISOString(),
      });
      setOcnValidations(prev => [res.data, ...prev]);
      toast.success('OCN-validering skapad');
      setOcnFormOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa validering');
    } finally {
      setOcnCreating(false);
    }
  };

  const sections = [
    { id: 'oversikt', label: 'Översikt' },
    { id: 'mal', label: 'Aktiva mål' },
    { id: 'narvaro', label: 'Närvaro' },
    { id: 'motesanteckningar', label: 'Mötesanteckningar' },
    { id: 'avvikelser', label: 'Avvikelser' },
    { id: 'ocn', label: `OCN (${ocnValidations.length})` },
    { id: 'tidslinje', label: 'Tidslinje' },
    ...(isAdmin ? [{ id: 'gdpr', label: 'GDPR' }] : []),
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Back + Header */}
        <div className="flex items-start gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/participants')} className="mt-1 rounded-full">
            <ArrowLeft size={16} className="mr-1" /> Tillbaka
          </Button>
          <div className="flex-1">
            <h1 className="text-4xl font-heading font-black tracking-tight">{participant.name}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              {client_name && (
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Building2 size={14} /> {client_name}
                </span>
              )}
              {participant.email && (
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Mail size={14} /> {participant.email}
                </span>
              )}
              {participant.phone && (
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Phone size={14} /> {participant.phone}
                </span>
              )}
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                participant.status === 'active' ? 'bg-success/20 text-success' : 'bg-muted text-muted-foreground'
              }`}>
                {participant.status === 'active' ? 'Aktiv' : participant.status}
              </span>
              {participant.consent_given && (
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-600">
                  Samtycke
                </span>
              )}
            </div>
          </div>
          {canWrite && participant.status !== 'raderad' && (
            <Button
              data-testid="open-close-dialog-btn"
              variant={participant.status === 'Avslutad' ? 'outline' : 'default'}
              size="sm"
              className="rounded-full shrink-0"
              onClick={openCloseDialog}
            >
              {participant.status === 'Avslutad'
                ? (participant.outcome_category ? 'Redigera utfall' : 'Registrera utfall')
                : 'Avsluta deltagare'}
            </Button>
          )}
        </div>

        {/* M6 — utfallsbanner (visas när deltagaren är avslutad) */}
        {participant.status === 'Avslutad' && (
          <Card className="border-l-4 border-l-primary">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Avslut</p>
                  <p className="text-sm">
                    <span className="font-bold">{participant.closure_reason || '—'}</span>
                    {participant.closure_date && (
                      <span className="text-muted-foreground"> · {formatDate(participant.closure_date)}</span>
                    )}
                  </p>
                  {participant.outcome_category ? (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div>
                        <span className="text-muted-foreground">Utfall: </span>
                        <span className="font-semibold">{OUTCOME_CATEGORY_LABELS[participant.outcome_category] || participant.outcome_category}</span>
                      </div>
                      {participant.outcome_employer && (
                        <div><span className="text-muted-foreground">Arbetsgivare: </span>{participant.outcome_employer}</div>
                      )}
                      {participant.outcome_employment_type && (
                        <div><span className="text-muted-foreground">Anställningstyp: </span>{EMPLOYMENT_TYPE_LABELS[participant.outcome_employment_type] || participant.outcome_employment_type}</div>
                      )}
                      {participant.outcome_employment_scope && (
                        <div><span className="text-muted-foreground">Omfattning: </span>{EMPLOYMENT_SCOPE_LABELS[participant.outcome_employment_scope] || participant.outcome_employment_scope}</div>
                      )}
                      {participant.outcome_education_type && (
                        <div><span className="text-muted-foreground">Utbildning: </span>{EDUCATION_TYPE_LABELS[participant.outcome_education_type] || participant.outcome_education_type}</div>
                      )}
                      {participant.outcome_education_provider && (
                        <div><span className="text-muted-foreground">Anordnare: </span>{participant.outcome_education_provider}</div>
                      )}
                      {participant.outcome_start_date && (
                        <div><span className="text-muted-foreground">Startdatum: </span>{formatDate(participant.outcome_start_date)}</div>
                      )}
                      {participant.outcome_notes && (
                        <div className="sm:col-span-2 text-muted-foreground italic">{participant.outcome_notes}</div>
                      )}
                    </div>
                  ) : (
                    canWrite && (
                      <p className="text-xs text-muted-foreground italic mt-1">
                        Inget utfall registrerat — klicka på "Registrera utfall" ovan.
                      </p>
                    )
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* N5 — Utfallsuppföljning (30/90/180 dagar) */}
        {participant.status === 'Avslutad' && (
          <OutcomeFollowups participantId={id} />
        )}

        {/* Processvy — Var är vi? */}
        <ParticipantProcessView participantId={id} />

        {/* O1 — Aktiva anpassningar och stöd */}
        {participant.status !== 'raderad' && (
          <AdaptationsSection participantId={id} currentUserRole={user?.role} />
        )}

        {/* Section nav */}
        <div className="flex gap-2 flex-wrap border-b pb-2">
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                activeSection === s.id
                  ? 'bg-primary text-[#333333]'
                  : 'bg-muted text-muted-foreground hover:bg-primary/20'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Sektion 1 — Översikt */}
        {activeSection === 'oversikt' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Stat cards */}
            <Card className="md:col-span-3 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x">
              <div className="p-6 flex flex-col gap-1">
                <span className="text-sm text-muted-foreground">Aktiva mål</span>
                <span className="text-3xl font-heading font-black">{goals.length}</span>
              </div>
              <div className="p-6 flex flex-col gap-1">
                <span className="text-sm text-muted-foreground">Närvaro (30 dagar)</span>
                <span className="text-3xl font-heading font-black">
                  {attendance_rate !== null ? `${attendance_rate}%` : '—'}
                </span>
              </div>
              <div className="p-6 flex flex-col gap-1">
                <span className="text-sm text-muted-foreground">Senaste möte</span>
                <span className="text-3xl font-heading font-black">
                  {meeting_notes.length > 0 ? formatDate(meeting_notes[0].motesdatum) : '—'}
                </span>
              </div>
            </Card>

            {/* Background */}
            {participant.background && (
              <Card className="md:col-span-2">
                <CardHeader><CardTitle className="font-heading font-bold">Bakgrund</CardTitle></CardHeader>
                <CardContent><p className="text-sm leading-relaxed">{participant.background}</p></CardContent>
              </Card>
            )}

            {/* Skills + challenges */}
            <Card>
              <CardHeader><CardTitle className="font-heading font-bold">Kompetenser & utmaningar</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {participant.skills && (
                  <div>
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1">Kompetenser</p>
                    <p className="text-sm">{participant.skills}</p>
                  </div>
                )}
                {participant.challenges && (
                  <div>
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1">Utmaningar</p>
                    <p className="text-sm">{participant.challenges}</p>
                  </div>
                )}
                {!participant.skills && !participant.challenges && (
                  <p className="text-sm text-muted-foreground">Ingen information tillagd.</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Sektion 2 — Aktiva mål */}
        {activeSection === 'mal' && (
          <div className="space-y-4">
            <h2 className="text-xl font-heading font-bold">Aktiva mål ({goals.length})</h2>
            {goals.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  Inga aktiva mål för denna deltagare.
                </CardContent>
              </Card>
            ) : (
              goals.map(goal => (
                <Card key={goal.id}>
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-bold text-base">{goal.title}</p>
                        {goal.description && <p className="text-sm text-muted-foreground mt-1">{goal.description}</p>}
                      </div>
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-success/20 text-success whitespace-nowrap">
                        {goal.status}
                      </span>
                    </div>
                    {typeof goal.progress === 'number' && (
                      <div>
                        <div className="flex justify-between text-xs text-muted-foreground mb-1">
                          <span>Framsteg</span>
                          <span>{goal.progress}%</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${goal.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {goal.target_date && (
                      <p className="text-xs text-muted-foreground">
                        Måldatum: {formatDate(goal.target_date)}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Sektion 3 — Närvaro */}
        {activeSection === 'narvaro' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-heading font-bold">Närvaro — senaste 30 dagarna</h2>
              {attendance_rate !== null && (
                <span className="flex items-center gap-2 text-lg font-bold">
                  <TrendingUp size={18} className="text-primary" />
                  {attendance_rate}% närvaro
                </span>
              )}
            </div>

            {attendance_last_30_days.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  Ingen närvaro registrerad de senaste 30 dagarna.
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {attendance_last_30_days.map(a => {
                  const s = statusLabel(a.status);
                  const IconComp = s.icon;
                  return (
                    <Card key={a.id} className="p-4 flex items-center gap-3">
                      <span className={`p-2 rounded-full ${s.color}`}>
                        <IconComp size={16} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold">{formatDate(a.date)}</p>
                        <p className={`text-xs font-bold ${s.color.split(' ')[1]}`}>{s.label}</p>
                        {a.notes && <p className="text-xs text-muted-foreground truncate">{a.notes}</p>}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Sektion 4 — Mötesanteckningar */}
        {activeSection === 'motesanteckningar' && (
          <div className="space-y-4">
            <h2 className="text-xl font-heading font-bold">Senaste mötesanteckningar</h2>
            {meeting_notes.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  Inga mötesanteckningar registrerade.
                </CardContent>
              </Card>
            ) : (
              meeting_notes.map(note => (
                <Card key={note.id}>
                  <CardContent className="p-5 space-y-2">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-bold text-base">{note.rubrik || 'Möte'}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(note.motesdatum)}</p>
                      </div>
                      {note.motestyp && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-muted text-muted-foreground whitespace-nowrap">
                          {note.motestyp}
                        </span>
                      )}
                    </div>
                    {note.anteckningar && (
                      <p className="text-sm leading-relaxed">{note.anteckningar}</p>
                    )}
                    {note.nasta_steg && note.nasta_steg.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1">Nästa steg</p>
                        <ul className="list-disc list-inside space-y-1">
                          {note.nasta_steg.map((steg, i) => (
                            <li key={i} className="text-sm">{steg}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Sektion 5 — Avvikelser */}
        {activeSection === 'avvikelser' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-heading font-bold">Avvikelser ({allDeviations.length})</h2>
              {canWrite && (
                <Button size="sm" className="rounded-full font-bold" onClick={() => setDeviationFormOpen(true)}>
                  <Plus size={16} className="mr-1" /> Registrera avvikelse
                </Button>
              )}
            </div>

            {allDeviations.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <AlertTriangle size={36} className="mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-muted-foreground">Inga avvikelser registrerade för denna deltagare.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {allDeviations.map(dev => (
                  <Card key={dev.id}>
                    <CardContent className="p-4 flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-bold text-sm">{TYP_LABELS[dev.typ] || dev.typ}</span>
                          <span className="text-xs text-muted-foreground">{new Date(dev.rapporterad_datum).toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{dev.beskrivning}</p>
                        {dev.atgardsbeskrivning && (
                          <p className="text-xs mt-1 text-muted-foreground italic">Åtgärd: {dev.atgardsbeskrivning}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${(ALLVARLIGHET_CONFIG[dev.allvarlighetsgrad] || {}).className || 'bg-muted'}`}>
                          {(ALLVARLIGHET_CONFIG[dev.allvarlighetsgrad] || {}).label || dev.allvarlighetsgrad}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${(DEV_STATUS_CONFIG[dev.status] || {}).className || 'bg-muted'}`}>
                          {(DEV_STATUS_CONFIG[dev.status] || {}).label || dev.status}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sektion OCN — Valideringar */}
        {activeSection === 'ocn' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-heading font-bold flex items-center gap-2">
                <Award size={20} /> OCN-valideringar ({ocnValidations.length})
              </h2>
              {canWrite && (
                <Button size="sm" className="rounded-full font-bold" onClick={() => setOcnFormOpen(true)}>
                  <Plus size={16} className="mr-1" /> Lägg till validering
                </Button>
              )}
            </div>

            {ocnValidations.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Award size={36} className="mx-auto mb-3 text-muted-foreground/20" />
                  <p className="text-muted-foreground">Inga OCN-valideringar registrerade.</p>
                  {canWrite && (
                    <Button size="sm" className="mt-4 rounded-full font-bold" onClick={() => setOcnFormOpen(true)}>
                      <Plus size={14} className="mr-1" /> Lägg till
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {ocnValidations.map(v => {
                  const sCfg = OCN_STATUS_CONFIG[v.status] || { label: v.status, className: 'bg-muted text-muted-foreground' };
                  const nCfg = OCN_NIVA_CONFIG[v.niva] || { label: v.niva, className: 'bg-muted text-muted-foreground' };
                  return (
                    <Card key={v.id}>
                      <CardContent className="p-4 flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-bold text-sm">{v.kompetensomrade}</span>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${nCfg.className}`}>{nCfg.label}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">Bedömare: {v.bedomare}</p>
                          {v.kommentar && <p className="text-xs text-muted-foreground mt-1 italic">{v.kommentar}</p>}
                          <p className="text-xs text-muted-foreground mt-1">
                            Start: {new Date(v.startdatum).toLocaleDateString('sv-SE')}
                            {v.slutdatum && ` → ${new Date(v.slutdatum).toLocaleDateString('sv-SE')}`}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${sCfg.className}`}>{sCfg.label}</span>
                          {v.poang != null && <span className="text-sm font-bold">{v.poang} p</span>}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Sektion 6 — GDPR (admin only) */}
        {activeSection === 'gdpr' && isAdmin && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-heading font-bold flex items-center gap-2">
                <ShieldAlert size={20} /> GDPR — Datarättigheter
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Hantera deltagarens personuppgifter i enlighet med GDPR.
              </p>
            </div>

            {/* Export */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-bold text-base mb-1">Exportera alla uppgifter</p>
                    <p className="text-sm text-muted-foreground">
                      Laddar ner all data kopplad till deltagaren som PDF (GDPR Art. 20 — rätten till dataportabilitet).
                    </p>
                  </div>
                  <Button variant="outline" onClick={handleGdprExport} className="rounded-full shrink-0">
                    <Download size={16} className="mr-2" />
                    Exportera data
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Delete */}
            <Card className="border-red-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-bold text-base text-red-600 mb-1">Anonymisera deltagare (GDPR Art. 17)</p>
                    <p className="text-sm text-muted-foreground">
                      Personuppgifter (namn, personnummer, e-post, telefon, anteckningar) anonymiseras permanent.
                      Strukturdata (datum, närvaro, status) bevaras för statistik. Åtgärden loggas i revisionsloggen.
                    </p>
                    <p className="text-xs text-red-500 font-semibold mt-2">
                      Åtgärden kan inte ångras.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="rounded-full shrink-0 border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => { setDeleteConfirmText(''); setGdprDeleteOpen(true); }}
                  >
                    <Trash2 size={16} className="mr-2" />
                    Radera
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Sektion 7 — Tidslinje */}
        {activeSection === 'tidslinje' && (
          <div className="space-y-4">
            <h2 className="text-xl font-heading font-bold">Tidslinje</h2>
            {timeline.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  Ingen händelsehistorik ännu.
                </CardContent>
              </Card>
            ) : (
              <div className="relative">
                <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />
                <div className="space-y-4 pl-12">
                  {timeline.map((event, i) => (
                    <div key={i} className="relative">
                      <div className={`absolute -left-7 p-1.5 rounded-full ${timelineColor(event.type)}`}>
                        {timelineIcon(event.type)}
                      </div>
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="font-bold text-sm">{event.title}</p>
                              {event.description && (
                                <p className="text-xs text-muted-foreground mt-0.5">{event.description}</p>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(event.date)}</p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* GDPR delete confirmation dialog */}
      <Dialog open={gdprDeleteOpen} onOpenChange={setGdprDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-bold text-red-600 flex items-center gap-2">
              <Trash2 size={20} /> Bekräfta radering
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-700 mb-1">Varning — persondata anonymiseras permanent</p>
              <p className="text-sm text-red-600">
                Du håller på att radera <strong>{participantName}</strong> enligt GDPR Art. 17.
                Persondata (namn, personnummer, kontakter, anteckningar) raderas;
                strukturdata (datum, status, närvaromönster) bevaras för statistik.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold" htmlFor="gdpr-delete-confirm">
                Skriv <span className="font-mono bg-muted px-1 rounded">{expectedConfirmPhrase}</span> för att bekräfta:
              </Label>
              <Input
                id="gdpr-delete-confirm"
                data-testid="gdpr-delete-confirm-input"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder={expectedConfirmPhrase}
                className="font-mono"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setGdprDeleteOpen(false)}
                disabled={gdprDeleting}
              >
                Avbryt
              </Button>
              <Button
                data-testid="gdpr-delete-confirm-btn"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full font-bold"
                onClick={handleGdprDelete}
                disabled={!expectedConfirmPhrase || deleteConfirmText !== expectedConfirmPhrase || gdprDeleting}
              >
                {gdprDeleting ? 'Anonymiserar...' : 'Anonymisera permanent'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* M6 — Close/outcome dialog */}
      <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-bold">
              {participant?.status === 'Avslutad' ? 'Redigera utfall' : 'Avsluta deltagare'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCloseSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="close-reason">Avslutsorsak *</Label>
                <select
                  id="close-reason"
                  data-testid="close-reason"
                  value={closeForm.closure_reason}
                  onChange={e => setCloseReason(e.target.value)}
                  className="h-10 w-full px-3 border border-input rounded-md bg-background text-sm"
                  required
                >
                  <option value="">Välj orsak…</option>
                  {CLOSURE_REASONS.map(r => (<option key={r} value={r}>{r}</option>))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="close-date">Avslutsdatum *</Label>
                <Input
                  id="close-date"
                  data-testid="close-date"
                  type="date"
                  value={closeForm.closure_date}
                  onChange={e => setCloseForm(p => ({ ...p, closure_date: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="close-comment">Kommentar</Label>
              <Textarea
                id="close-comment"
                rows={2}
                value={closeForm.status_comment}
                onChange={e => setCloseForm(p => ({ ...p, status_comment: e.target.value }))}
                placeholder="Frivillig anteckning om avslutet…"
              />
            </div>

            {/* Utfallssektion (visas när category är satt) */}
            {closeForm.outcome_category && (
              <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
                <div className="flex items-baseline justify-between">
                  <p className="text-sm font-bold">Utfall — {OUTCOME_CATEGORY_LABELS[closeForm.outcome_category]}</p>
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:underline"
                    onClick={() => setCloseForm(p => ({ ...p, outcome_category: '' }))}
                  >
                    Rensa
                  </button>
                </div>

                {closeForm.outcome_category === 'arbete' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="out-employer">Arbetsgivare *</Label>
                      <Input
                        id="out-employer"
                        data-testid="out-employer"
                        value={closeForm.outcome_employer}
                        onChange={e => setCloseForm(p => ({ ...p, outcome_employer: e.target.value }))}
                        placeholder="Företagsnamn"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="out-emptype">Anställningstyp</Label>
                      <select
                        id="out-emptype"
                        value={closeForm.outcome_employment_type}
                        onChange={e => setCloseForm(p => ({ ...p, outcome_employment_type: e.target.value }))}
                        className="h-10 w-full px-3 border border-input rounded-md bg-background text-sm"
                      >
                        <option value="">—</option>
                        {Object.entries(EMPLOYMENT_TYPE_LABELS).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="out-empscope">Omfattning</Label>
                      <select
                        id="out-empscope"
                        value={closeForm.outcome_employment_scope}
                        onChange={e => setCloseForm(p => ({ ...p, outcome_employment_scope: e.target.value }))}
                        className="h-10 w-full px-3 border border-input rounded-md bg-background text-sm"
                      >
                        <option value="">—</option>
                        {Object.entries(EMPLOYMENT_SCOPE_LABELS).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
                      </select>
                    </div>
                  </div>
                )}

                {closeForm.outcome_category === 'studier' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="out-edutype">Utbildningstyp *</Label>
                      <select
                        id="out-edutype"
                        data-testid="out-edutype"
                        value={closeForm.outcome_education_type}
                        onChange={e => setCloseForm(p => ({ ...p, outcome_education_type: e.target.value }))}
                        className="h-10 w-full px-3 border border-input rounded-md bg-background text-sm"
                        required
                      >
                        <option value="">Välj…</option>
                        {Object.entries(EDUCATION_TYPE_LABELS).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="out-eduprov">Anordnare</Label>
                      <Input
                        id="out-eduprov"
                        value={closeForm.outcome_education_provider}
                        onChange={e => setCloseForm(p => ({ ...p, outcome_education_provider: e.target.value }))}
                        placeholder="Skola/anordnare"
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="out-start">Startdatum</Label>
                    <Input
                      id="out-start"
                      type="date"
                      value={closeForm.outcome_start_date}
                      onChange={e => setCloseForm(p => ({ ...p, outcome_start_date: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="out-notes">Anteckningar</Label>
                  <Textarea
                    id="out-notes"
                    rows={2}
                    value={closeForm.outcome_notes}
                    onChange={e => setCloseForm(p => ({ ...p, outcome_notes: e.target.value }))}
                    placeholder="Frivilliga anteckningar om utfallet…"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setCloseDialogOpen(false)} disabled={closeSubmitting}>
                Avbryt
              </Button>
              <Button
                type="submit"
                data-testid="close-submit"
                className="flex-1 rounded-full font-bold"
                disabled={closeSubmitting}
              >
                {closeSubmitting ? 'Sparar…' : (participant?.status === 'Avslutad' ? 'Spara utfall' : 'Avsluta deltagare')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* OCN create dialog */}
      <Dialog open={ocnFormOpen} onOpenChange={setOcnFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-bold">Ny OCN-validering</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateOcn} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Kompetensområde *</Label>
                <Select value={ocnForm.kompetensomrade} onValueChange={v => setOcnForm(p => ({ ...p, kompetensomrade: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Kommunikation','Teamarbete','Problemlösning','Tidsplanering','Ledarskap','Anpassningsförmåga','Teknisk kompetens','Kundservice','Initiativförmåga','Kvalitetsmedvetenhet','Självständighet','Analytisk förmåga','Kreativitet','Flexibilitet','Stresshantering'].map(o => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Nivå *</Label>
                <Select value={ocnForm.niva} onValueChange={v => setOcnForm(p => ({ ...p, niva: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bas">Bas</SelectItem>
                    <SelectItem value="mellan">Mellan</SelectItem>
                    <SelectItem value="avancerad">Avancerad</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Bedömare *</Label>
              <Input value={ocnForm.bedomare} onChange={e => setOcnForm(p => ({ ...p, bedomare: e.target.value }))} required />
            </div>
            <div className="space-y-1.5">
              <Label>Valideringsmålsättning *</Label>
              <Textarea rows={3} value={ocnForm.valideringsmalsattning} onChange={e => setOcnForm(p => ({ ...p, valideringsmalsattning: e.target.value }))} placeholder="Beskriv målsättningen..." required />
            </div>
            <div className="space-y-1.5">
              <Label>Startdatum *</Label>
              <Input type="date" value={ocnForm.startdatum} onChange={e => setOcnForm(p => ({ ...p, startdatum: e.target.value }))} required />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setOcnFormOpen(false)}>Avbryt</Button>
              <Button type="submit" className="flex-1 rounded-full font-bold" disabled={ocnCreating}>
                {ocnCreating ? 'Skapar...' : 'Skapa'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create deviation dialog */}
      <Dialog open={deviationFormOpen} onOpenChange={setDeviationFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold">Registrera avvikelse</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateDeviation} className="space-y-4">
            <div className="space-y-2">
              <Label>Typ *</Label>
              <Select value={devForm.typ} onValueChange={(v) => setDevForm({ ...devForm, typ: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Välj typ" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TYP_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Allvarlighetsgrad</Label>
              <Select value={devForm.allvarlighetsgrad} onValueChange={(v) => setDevForm({ ...devForm, allvarlighetsgrad: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ALLVARLIGHET_CONFIG).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Beskrivning *</Label>
              <Textarea
                value={devForm.beskrivning}
                onChange={(e) => setDevForm({ ...devForm, beskrivning: e.target.value })}
                rows={4}
                placeholder="Beskriv vad som hänt..."
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={devSubmitting} className="flex-1 rounded-full font-bold">
                {devSubmitting ? 'Sparar...' : 'Registrera'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setDeviationFormOpen(false)} className="rounded-full">
                Avbryt
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ParticipantProfilePage;
